import { TEMPLATE_CTUI_TAG } from 'appConstants';
import { JSONSchema6, JSONSchema6Definition } from 'json-schema';
import React from 'react';
import { AjvError } from 'react-jsonschema-form';
import { AbridgedBpitTemplate, BpitTemplateFilters } from 'types';

import { MiscUtils } from './MiscUtils';

type BpitElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

type BpitEvent<R extends BpitElement> = React.SyntheticEvent<R>;

const rxArrayIndex = /\[\d+\]/;

const BpitUtils = {
  /**
   * Check if given bpit template id is valid against the given collection
   */
  checkForValidBpitTemplate: (
    allTemplates: AbridgedBpitTemplate[],
    draftTemplate: string,
  ): boolean => allTemplates.some((item) => item.templateId === draftTemplate),

  /**
   * Returns the array of all the bpitDeviceSelector widget keys
   * in the uiSchema
   */
  collectDeviceSelectorKeys(uiSchema: {}): string[] {
    return BpitUtils.collectBpitWidgetKeys(uiSchema, 'bpitDeviceSelector');
  },

  /**
   * Returns the array of all the bpitFileAttachment widget keys
   * in the uiSchema
   */
  collectBpitAttachmentSelectorKeys(uiSchema: {}): string[] {
    return BpitUtils.collectBpitWidgetKeys(uiSchema, 'bpitFileAttachment');
  },

  /**
   * Recursively traverse uiSchema, collecting and returning an array of all
   * found given BPIT widget keys
   */
  collectBpitWidgetKeys(
    uiSchema: {},
    widgetName: string,
    deviceKeys: string[] = [],
    prefix: string = '',
  ): string[] {
    Object.keys(uiSchema).forEach((key: string) => {
      const prop = uiSchema[key];
      if (prop['ui:widget'] === widgetName) {
        deviceKeys.push((prefix ? prefix + '.' : '') + key);
      } else if (MiscUtils.isObject(prop)) {
        BpitUtils.collectBpitWidgetKeys(
          prop,
          widgetName,
          deviceKeys,
          prefix ? prefix + '.' + key : key,
        );
      }
    });

    return deviceKeys;
  },

  /**
   * Provides a JSON Schema Form Error Transform which removes errors related
   * to custom BPIT Device Selector widget
   */
  consumeDeviceSelectorErrors(deviceSelectorKeys: string[] = []) {
    return (errors: AjvError[]) =>
      errors.filter((error) => {
        /*
         * AdditionalProperty errors are expected on schemas with dependencies
         * This is caused by an edge case where a dependent field is populated
         * but then a different field is selected, orphaning the previous data
         */

        if (
          error.name === 'additionalProperties' &&
          error.message === 'is an invalid additional property'
        ) {
          return false;
        }

        // Include any other error not belonging to a device selector field
        const errorProp = error.property.substr(1).replace(rxArrayIndex, '');
        if (
          !deviceSelectorKeys.some((device) => {
            return device.replace('.items', '') === errorProp;
          })
        ) {
          return true;
        }

        /*
         * -- Ignore DeviceSelector errors where value doesn't match an enum --
         * Enums are full devices and selected values are only string, so they
         * will never match
         */
        if (
          error.name === 'enum' &&
          error.message === 'should be equal to one of the allowed values'
        ) {
          return false;
        }

        /*
         * -- Ignore DeviceSelector errors where type should be string --
         * This probably means that the DeviceSelector is a multi-select and
         * returned an array instead of a string. The array what's wanted
         */

        if (error.name === 'type' && error.message === 'should be string') {
          return false;
        }

        return true;
      });
  },

  /**
   * Returns an input event handler which will execute the provided callback
   * with the event's new current target value
   */
  executeOnUpdate: <T extends BpitElement>(callback: Function) => (
    event: BpitEvent<T>,
  ): void => callback(event.currentTarget.value),

  getBpitTemplateFilters: (
    accountID: string,
    category: string,
    includeDevices: boolean,
    devices: string[],
    subcategory: string,
  ): BpitTemplateFilters => {
    const resourceID = includeDevices ? devices : [];
    const tags = [category, subcategory, TEMPLATE_CTUI_TAG].map(
      MiscUtils.toSnakecase,
    );
    if (!resourceID || resourceID.length <= 0) {
      return { accountID, tags, resourceID: null };
    }
    return { accountID, resourceID, tags };
  },
  /**
   * Returns the value of the deeply nested property within a schema object at
   * the specified string path
   */
  getDeepProperty: (schema: JSONSchema6, key: string) => {
    const deepKey = 'properties.' + key.replace(/\.(?!items)/g, '.properties.');
    return MiscUtils.get<JSONSchema6Definition>(schema, deepKey, {});
  },

  /** Returns array of property paths given a PathSchema object */
  getPathKeys: (obj: {}, keys: string[] = []) => {
    Object.keys(obj).forEach((key) => {
      if (key === '$name' && obj[key] !== '') {
        keys.push(obj[key]);
      } else {
        BpitUtils.getPathKeys(obj[key], keys);
      }
    });
    return keys;
  },

  /** Prevents keyboard event if it equals space */
  preventWhitespace: (event: React.KeyboardEvent<BpitElement>) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  },

  /**
   * Mutates given `data` object, removing any nested property that is not
   * present in whitelist. Accepts `prefix` to allow recursion for key
   * comparison.
   */
  purgeExtraProps: (data: {}, whitelist: string[], prefix = '') => {
    Object.keys(data).forEach((key) => {
      if (
        !whitelist.includes(`${prefix}${key}`) &&
        !whitelist.includes(`${prefix}${key}.0`) // Check for array paths
      ) {
        delete data[key];
      } else if (
        MiscUtils.isObject(data[key]) ||
        MiscUtils.isArrayOfObjects(data[key])
      ) {
        BpitUtils.purgeExtraProps(data[key], whitelist, `${prefix}${key}.`);
      }
    });
  },

  /**
   * Returns an input event handler which will apply a transform function on
   * the current target's value
   */
  transformValue: <T extends BpitElement>(callback: Function) => (
    event: BpitEvent<T>,
  ) => (event.currentTarget.value = callback(event.currentTarget.value)),
};

export { BpitUtils };
